import { useQuery } from '@tanstack/vue-query';
import { computed, Ref, unref } from 'vue';
import axios from '@/plugins/w@custom/axios/models/axios';
import moment from 'moment';
import timelineUtils from '@/plugins/tm@dashboard/tasks/_utils/timeline.utils';
import {
  detectCollision,
  calculateTimeblockDuration,
  getCollisionSegments,
  type Timeblock,
  type CollisionInfo,
} from './timeblock.utils';

interface Employee {
  id: number;
  name: string;
  surname: string;
  work_start: number;
  work_end: number;
}

export function useEmployeeTimeline(
  employee: Ref<Employee | null>,
  date: Ref<Date | string | null>,
  currentTimeblock: Ref<Timeblock | null>,
  otherLocalTimeblocks?: Ref<Timeblock[]>,
  editingBlockerId?: Ref<number | null>,
  draftTimeblocks?: Ref<Timeblock[]>,
  allEditingTimeblockIds?: Ref<number[]>,
) {
  // Fetch employee's tasks for the selected date
  const {
    data: tasksData,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: computed(() => [
      'employee-timeline',
      unref(employee)?.id,
      unref(date) ? moment(unref(date)).format('YYYY-MM-DD') : null,
    ]),
    queryFn: async () => {
      const emp = unref(employee);
      const d = unref(date);

      if (!emp?.id || !d) {
        return null;
      }

      const dateStr = moment(d).format('YYYY-MM-DD');
      const queryParams = new URLSearchParams({
        start: dateStr,
        end: dateStr,
        employee_id: emp.id.toString(),
      }).toString();

      const response = await axios.get_auth(`v1/subtasks?${queryParams}`);
      return response;
    },
    enabled: computed(() => {
      const emp = unref(employee);
      const d = unref(date);
      return !!(emp?.id && d);
    }),
  });

  //   const {
  //     data: conceptTasksData,
  //     isLoading: conceptTasksLoading,
  //     error: conceptTasksError,
  //     refetch: conceptTasksRefetch,
  //   } = useQuery({
  //     queryKey: computed(() => [
  //       'concept-employee-timeline',
  //       unref(employee)?.id,
  //       unref(date) ? moment(unref(date)).format('YYYY-MM-DD') : null,
  //     ]),
  //     queryFn: async () => {
  //       const emp = unref(employee);
  //       const d = unref(date);

  //       if (!emp?.id || !d) {
  //         return null;
  //       }

  //       const dateStr = moment(d).format('YYYY-MM-DD');
  //       const queryParams = new URLSearchParams({
  //         start: dateStr,
  //         end: dateStr,
  //         employee_id: emp.id.toString(),
  //         type: 'draft',
  //       }).toString();

  //       const response = await axios.get_auth(`v1/subtasks?${queryParams}`);
  //       return response;
  //     },
  //     enabled: computed(() => {
  //       const emp = unref(employee);
  //       const d = unref(date);
  //       return !!(emp?.id && d);
  //     }),
  //   });

  // Generate timeline tasks from fetched data
  const timelineTasks = computed(() => {
    const data = unref(tasksData);
    // const conceptData = unref(conceptTasksData);
    const emp = unref(employee);
    const d = unref(date);
    const excludeBlockerId = editingBlockerId ? unref(editingBlockerId) : null;
    const draftTbs = draftTimeblocks ? unref(draftTimeblocks) : [];

    if (!data || !emp || !d) {
      return [];
    }

    const subtasks = [
      ...(data.subtasks || []),
      ...(data.blockers || []),
      //   ...(conceptData.subtasks || []),
    ];

    const selectedDateStr = moment(d).format('YYYY-MM-DD');

    try {
      // Process timeblocks directly instead of using timeline utils
      // which is designed for multi-day views
      const tasks: any[] = [];

      subtasks.forEach((subtask) => {
        // Check if this is a blocker/adhoc/etc with direct start/end fields (no timeblocks array)
        if (
          !subtask.timeblocks &&
          subtask.start &&
          subtask.end &&
          [
            'blocker',
            'adhoc',
            'home_office',
            'vacation',
            'free_day',
            'office365_blocker',
          ].includes(subtask.type)
        ) {
          // Skip the blocker being edited
          if (excludeBlockerId && subtask.id === excludeBlockerId) {
            return;
          }

          const blockerDateStr = moment(subtask.start).format('YYYY-MM-DD');

          if (blockerDateStr === selectedDateStr) {
            const type = subtask.type;

            const task = {
              id: subtask.id,
              start: new Date(subtask.start),
              end: new Date(subtask.end),
              master_task: null,
              date: new Date(subtask.start),
              project_code: null,
              subtask: subtask,
              is_draft: !!subtask.is_draft,
              blocker: subtask,
              type,
              _timeblock_id: null,
              _timeblock_is_finished: subtask.is_finished || false,
              _real_start: new Date(subtask.start),
              _real_end: new Date(subtask.end),
            };

            tasks.push(task);
          }
        } else {
          // Determine the correct timeblocks array based on task type
          let timeblocks: any[] | undefined;

          // For draft tasks with employees array, get timeblocks from the matching employee
          if (subtask.is_draft && Array.isArray(subtask.employees)) {
            const employeeData = subtask.employees.find(
              (empData: any) => Number(empData.id) === Number(emp.id),
            );
            timeblocks = employeeData?.timeblocks;
          } else {
            // For regular tasks, use root-level timeblocks array
            timeblocks = subtask.timeblocks;
          }

          // Process timeblocks (same logic for both regular and concept tasks)
          timeblocks?.forEach((timeblock: any) => {
            const timeblockDateStr = moment(timeblock.start).format(
              'YYYY-MM-DD',
            );

            // Only include timeblocks for the selected date
            if (timeblockDateStr === selectedDateStr) {
              let type = 'subtask';
              if (subtask.type === 'office365') type = 'office365';

              const task = {
                id: subtask.id,
                start: new Date(timeblock.start),
                end: new Date(timeblock.end),
                master_task: subtask.master_task,
                date: new Date(timeblock.start),
                project_code: subtask.project_code,
                subtask: subtask,
                is_draft: !!subtask.is_draft,
                blocker: undefined,
                type,
                _timeblock_id: timeblock.id,
                _timeblock_is_finished: timeblock.is_finished,
                _real_start: new Date(timeblock.start),
                _real_end: new Date(timeblock.end),
              };

              tasks.push(task);
            }
          });
        }
      });

      // Process draft timeblocks from other employees in the same draft subtask
      draftTbs.forEach((timeblock: any) => {
        // Skip if timeblock doesn't have required fields
        if (!timeblock.start || !timeblock.end) {
          return;
        }

        const timeblockDateStr = moment(timeblock.start).format('YYYY-MM-DD');

        // Only include timeblocks for the selected date
        if (timeblockDateStr === selectedDateStr) {
          const task = {
            id: null, // Draft timeblock doesn't have a subtask ID
            start: new Date(timeblock.start),
            end: new Date(timeblock.end),
            master_task: null,
            date: new Date(timeblock.start),
            project_code: null,
            subtask: null,
            is_draft: true,
            blocker: undefined,
            type: 'draft_timeblock',
            _timeblock_id: timeblock.id || null,
            _timeblock_is_finished: timeblock.is_finished || false,
            _real_start: new Date(timeblock.start),
            _real_end: new Date(timeblock.end),
            _employee_id: timeblock.employee_id,
          };

          tasks.push(task);
        }
      });

      return tasks;
    } catch (error) {
      console.error('Error generating timeline tasks:', error);
      return [];
    }
  });

  // Calculate scheduled time for the day (in hours)
  const scheduledTime = computed(() => {
    const tasks = unref(timelineTasks);
    const d = unref(date);

    if (!tasks || !d) return 0;

    // Get IDs of timeblocks being edited locally to exclude them from scheduled time
    const editingTimeblockIds = allEditingTimeblockIds
      ? new Set(unref(allEditingTimeblockIds).filter((id) => id != null))
      : new Set();

    let totalMinutes = 0;

    tasks.forEach((task) => {
      if (
        task.type === 'non-working-hours' ||
        task.subtask?.is_finished ||
        task.blocker?.is_finished
      ) {
        return;
      }

      // Skip timeblocks that are being edited locally to avoid double-counting
      if (task._timeblock_id && editingTimeblockIds.has(task._timeblock_id)) {
        return;
      }

      totalMinutes += timelineUtils._getDifferenceInMinutes(
        task._real_end,
        task._real_start,
      );
    });

    return (totalMinutes / 60).toFixed(1);
  });

  // Calculate total planned time including local edits (for display purposes)
  const totalPlannedTime = computed(() => {
    const scheduled = parseFloat(String(unref(scheduledTime)));
    
    // Calculate duration of other local unsaved timeblocks for the same date
    let localTimeblocksHours = 0;
    if (otherLocalTimeblocks) {
      const localTbs = unref(otherLocalTimeblocks) || [];
      localTbs.forEach((tb) => {
        // Only count timeblocks with complete time data
        if (tb.starts_at_time && tb.ends_at_time) {
          localTimeblocksHours += calculateTimeblockDuration(tb);
        }
      });
    }

    // Also include the current timeblock if it has both start and end times
    const currentTb = unref(currentTimeblock);
    if (currentTb?.starts_at_time && currentTb?.ends_at_time) {
      localTimeblocksHours += calculateTimeblockDuration(currentTb);
    }

    return (scheduled + localTimeblocksHours).toFixed(1);
  });

  // Calculate free/unscheduled time for the day (in hours)
  const freeTime = computed(() => {
    const emp = unref(employee);
    const d = unref(date);
    const totalPlanned = parseFloat(String(unref(totalPlannedTime)));

    if (!emp || !d) return '0';

    const dailyCapacityHours = emp.work_end - emp.work_start;

    return (dailyCapacityHours - totalPlanned).toFixed(1);
  });

  // Detect collisions with current timeblock
  const collision = computed<CollisionInfo>(() => {
    const timeblock = unref(currentTimeblock);
    const tasks = unref(timelineTasks);

    if (!timeblock || !tasks) {
      return {
        hasCollision: false,
        startCollides: false,
        endCollides: false,
        collidingTasks: [],
      };
    }

    return detectCollision(timeblock, tasks);
  });

  // Get collision segments for timeline visualization
  const collisionSegments = computed(() => {
    const timeblock = unref(currentTimeblock);
    const tasks = unref(timelineTasks);

    if (!timeblock || !tasks) {
      return [];
    }

    return getCollisionSegments(timeblock, tasks);
  });

  // Calculate current timeblock duration
  const timeblockDuration = computed(() => {
    const timeblock = unref(currentTimeblock);
    if (!timeblock) return 0;
    return calculateTimeblockDuration(timeblock);
  });

  return {
    // Query states
    isLoading,
    error,
    refetch,

    // conceptTasksLoading,
    // conceptTasksError,
    // conceptTasksRefetch,

    // Timeline data
    timelineTasks,
    scheduledTime,
    totalPlannedTime,
    freeTime,

    // Collision detection
    collision,
    collisionSegments,
    timeblockDuration,
  };
}
